import React, { createContext, useContext, useState, useCallback } from "react";

const UploadContext = createContext();

export const useUpload = () => {
  const context = useContext(UploadContext);
  if (!context) {
    throw new Error("useUpload must be used within an UploadProvider");
  }
  return context;
};

export const UploadProvider = ({ children }) => {
  const [uploadStatus, setUploadStatus] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isProcessingMultiple, setIsProcessingMultiple] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // File queue: array of { id, name, size, status: 'queued'|'uploading'|'success'|'error', error? }
  const [fileQueue, setFileQueue] = useState([]);

  // Delete queue: array of { id, name, path, status: 'queued'|'deleting'|'success'|'error', error? }
  const [deleteQueue, setDeleteQueue] = useState([]);

  const initFileQueue = useCallback((files) => {
    const queue = files.map((file, index) => ({
      id: `${Date.now()}-${index}`,
      name: file.name,
      size: file.size,
      status: "queued",
      error: null,
    }));
    setFileQueue(queue);
    return queue;
  }, []);

  const updateFileInQueue = useCallback((index, updates) => {
    setFileQueue((prev) => {
      const next = [...prev];
      if (next[index]) {
        next[index] = { ...next[index], ...updates };
      }
      return next;
    });
  }, []);

  const clearFileQueue = useCallback(() => {
    setFileQueue([]);
  }, []);

  const initDeleteQueue = useCallback((filePaths, allFiles) => {
    const queue = filePaths.map((filePath, index) => {
      const file = allFiles.find((f) => f.path === filePath);
      return {
        id: `del-${Date.now()}-${index}`,
        name: file ? file.name : filePath.split("/").pop(),
        path: filePath,
        status: "queued",
        error: null,
      };
    });
    setDeleteQueue(queue);
    return queue;
  }, []);

  const updateFileInDeleteQueue = useCallback((index, updates) => {
    setDeleteQueue((prev) => {
      const next = [...prev];
      if (next[index]) {
        next[index] = { ...next[index], ...updates };
      }
      return next;
    });
  }, []);

  const clearDeleteQueue = useCallback(() => {
    setDeleteQueue([]);
  }, []);

  const clearUploadStatus = () => {
    setUploadStatus(null);
    setUploading(false);
    setUploadProgress(0);
    setIsProcessingMultiple(false);
    setIsDragOver(false);
    setLoading(false);
    setError(null);
  };

  const setUploadingState = (isUploading, progress = 0) => {
    setUploading(isUploading);
    setUploadProgress(progress);
  };

  const setUploadStatusMessage = (status) => {
    setUploadStatus(status);
  };

  const value = {
    uploadStatus,
    uploading,
    uploadProgress,
    isProcessingMultiple,
    isDragOver,
    loading,
    error,
    fileQueue,
    deleteQueue,
    clearUploadStatus,
    setUploadingState,
    setUploadStatusMessage,
    setIsProcessingMultiple,
    setIsDragOver,
    setLoading,
    setError,
    initFileQueue,
    updateFileInQueue,
    clearFileQueue,
    initDeleteQueue,
    updateFileInDeleteQueue,
    clearDeleteQueue,
  };

  return (
    <UploadContext.Provider value={value}>{children}</UploadContext.Provider>
  );
};
